import React, { useMemo } from 'react';
import { Holding, Currency, Loan } from '../types';
import { convertCurrency, formatCurrency } from '../utils/currency';
import { getCountry } from '../utils/countries';
import { Card, CardHeader, CardContent } from './ui/Card';
interface AllocationChartsProps {
  holdings: Holding[];
  baseCurrency: Currency;
  partnerNames: [string, string];
  loans?: Loan[];
}
interface Segment {
  label: string;
  value: number;
  color: string;
  textColor: string;
}
function HorizontalStackedBar({
  segments,
  total,
  baseCurrency
}: {
  segments: Segment[];
  total: number;
  baseCurrency: Currency;
}) {
  if (total === 0 || segments.length === 0) {
    return <div className="text-sm text-slate-400 italic py-3" data-id="element-18">
        No holdings to display.
      </div>;
  }
  return <div className="space-y-3" data-id="element-19">
      {/* Bar */}
      <div className="h-8 rounded-lg overflow-hidden flex bg-slate-100" data-id="element-20">
        {segments.map((seg, i) => {
        const pct = seg.value / total * 100;
        if (pct < 0.5) return null;
        return <div key={i} className="h-full flex items-center justify-center text-xs font-bold transition-all relative group" style={{
          width: `${pct}%`,
          backgroundColor: seg.color,
          color: seg.textColor
        }} title={`${seg.label}: ${formatCurrency(seg.value, baseCurrency)} (${pct.toFixed(1)}%)`} data-id="element-21">
              {pct > 8 && <span className="truncate px-1" data-id="element-22">{pct.toFixed(0)}%</span>}
            </div>;
      })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-5 gap-y-2" data-id="element-23">
        {segments.map((seg, i) => {
        const pct = seg.value / total * 100;
        return <div key={i} className="flex items-center gap-2 min-w-0" data-id="element-24">
              <div className="w-3 h-3 rounded-sm shrink-0" style={{
            backgroundColor: seg.color
          }} data-id="element-25" />
              <span className="text-sm text-slate-700 font-medium truncate" data-id="element-26">
                {seg.label}
              </span>
              <span className="text-sm text-slate-500 tabular-nums" data-id="element-27">
                {formatCurrency(seg.value, baseCurrency)}
              </span>
              <span className="text-xs text-slate-400 tabular-nums" data-id="element-28">
                ({pct.toFixed(1)}%)
              </span>
            </div>;
      })}
      </div>
    </div>;
}
export function AllocationCharts({
  holdings,
  baseCurrency,
  partnerNames,
  loans = []
}: AllocationChartsProps) {
  const totalAssets = useMemo(() => {
    return holdings.reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, baseCurrency), 0);
  }, [holdings, baseCurrency]);
  // 1. Asset Class: Liquid, Real Estate, Other Illiquid
  const assetClassSegments = useMemo<Segment[]>(() => {
    const liquid = holdings.filter(h => h.type === 'Liquid').reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, baseCurrency), 0);
    const realEstate = holdings.filter(h => h.isRealEstate).reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, baseCurrency), 0);
    const otherIlliquid = holdings.filter(h => h.type === 'Illiquid' && !h.isRealEstate).reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, baseCurrency), 0);
    const segs: Segment[] = [];
    if (liquid > 0) segs.push({
      label: 'Liquid',
      value: liquid,
      color: '#3b82f6',
      textColor: '#fff'
    });
    if (realEstate > 0) segs.push({
      label: 'Real Estate',
      value: realEstate,
      color: '#6366f1',
      textColor: '#fff'
    });
    if (otherIlliquid > 0) segs.push({
      label: 'Other Illiquid',
      value: otherIlliquid,
      color: '#64748b',
      textColor: '#fff'
    });
    return segs;
  }, [holdings, baseCurrency]);
  const hasRealEstate = holdings.some(h => h.isRealEstate);
  // 2. Real Estate: Equity vs Debt
  const reEquityDebtSegments = useMemo<Segment[]>(() => {
    if (!hasRealEstate) return [];
    const reHoldings = holdings.filter(h => h.isRealEstate);
    let grossValue = 0;
    let totalDebt = 0;
    for (const h of reHoldings) {
      grossValue += convertCurrency(h.amount, h.currency, baseCurrency);
      if (h.linkedLoanId) {
        const loan = loans.find(l => l.id === h.linkedLoanId);
        if (loan && loan.isActive) {
          const principal = Math.max(0, loan.totalAmount - loan.depositAmount);
          totalDebt += convertCurrency(principal, loan.currency, baseCurrency);
        }
      }
    }
    const equity = Math.max(0, grossValue - totalDebt);
    const segs: Segment[] = [];
    if (equity > 0) segs.push({
      label: 'Equity (Owned)',
      value: equity,
      color: '#10b981',
      textColor: '#fff'
    });
    if (totalDebt > 0) segs.push({
      label: 'Mortgage Debt',
      value: totalDebt,
      color: '#f43f5e',
      textColor: '#fff'
    });
    return segs;
  }, [holdings, loans, baseCurrency, hasRealEstate]);
  const reGrossValue = useMemo(() => {
    return holdings.filter(h => h.isRealEstate).reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, baseCurrency), 0);
  }, [holdings, baseCurrency]);
  // 1. Liquid vs Illiquid
  const typeSegments = useMemo<Segment[]>(() => {
    const liquid = holdings.filter(h => h.type === 'Liquid').reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, baseCurrency), 0);
    const illiquid = holdings.filter(h => h.type === 'Illiquid').reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, baseCurrency), 0);
    const segs: Segment[] = [];
    if (liquid > 0) segs.push({
      label: 'Liquid',
      value: liquid,
      color: '#3b82f6',
      textColor: '#fff'
    });
    if (illiquid > 0) segs.push({
      label: 'Illiquid',
      value: illiquid,
      color: '#64748b',
      textColor: '#fff'
    });
    return segs;
  }, [holdings, baseCurrency]);
  // 2. By Currency
  const dynamicCurrencyColors = ['#2563eb', '#475569', '#1e40af', '#0f172a', '#0891b2', '#4f46e5', '#7c3aed', '#0284c7' // light cyan
  ];
  const currencySegments = useMemo<Segment[]>(() => {
    const byCurrency: Record<string, number> = {};
    holdings.forEach(h => {
      const converted = convertCurrency(h.amount, h.currency, baseCurrency);
      byCurrency[h.currency] = (byCurrency[h.currency] || 0) + converted;
    });
    return Object.entries(byCurrency).sort(([, a], [, b]) => b - a).map(([currency, value], i) => ({
      label: currency,
      value,
      color: dynamicCurrencyColors[i % dynamicCurrencyColors.length],
      textColor: '#fff'
    }));
  }, [holdings, baseCurrency]);
  // 3. By Country
  const countryColors = ['#2563eb', '#475569', '#1e40af', '#64748b', '#1e3a5f', '#334155', '#3b82f6', '#0f172a'];
  const countrySegments = useMemo<Segment[]>(() => {
    const byCountry: Record<string, number> = {};
    holdings.forEach(h => {
      const code = h.country || 'Unknown';
      const converted = convertCurrency(h.amount, h.currency, baseCurrency);
      byCountry[code] = (byCountry[code] || 0) + converted;
    });
    return Object.entries(byCountry).sort(([, a], [, b]) => b - a).map(([code, value], i) => {
      const country = getCountry(code);
      return {
        label: `${country.flag} ${country.name}`,
        value,
        color: countryColors[i % countryColors.length],
        textColor: '#fff'
      };
    });
  }, [holdings, baseCurrency]);
  // 4. By Owner (Partner 1, Partner 2, Mutual)
  const ownerColors: Record<string, {
    bg: string;
    text: string;
  }> = {
    [partnerNames[0]]: {
      bg: '#6366f1',
      text: '#fff'
    },
    [partnerNames[1]]: {
      bg: '#ec4899',
      text: '#fff'
    },
    Mutual: {
      bg: '#14b8a6',
      text: '#fff'
    }
  };
  const ownerSegments = useMemo<Segment[]>(() => {
    const byOwner: Record<string, number> = {};
    holdings.forEach(h => {
      const converted = convertCurrency(h.amount, h.currency, baseCurrency);
      const key = h.person === 'Joint' ? 'Mutual' : h.person;
      byOwner[key] = (byOwner[key] || 0) + converted;
    });
    // Order: partner1, partner2, mutual
    const order = [partnerNames[0], partnerNames[1], 'Mutual'];
    return order.filter(key => (byOwner[key] || 0) > 0).map(key => ({
      label: key,
      value: byOwner[key],
      color: ownerColors[key]?.bg || '#64748b',
      textColor: ownerColors[key]?.text || '#fff'
    }));
  }, [holdings, baseCurrency, partnerNames]);
  return <Card data-id="element-29">
      <CardHeader title="Portfolio Allocation" subtitle={`Breakdown of ${formatCurrency(totalAssets, baseCurrency)} total assets`} data-id="element-30" />
      <CardContent className="space-y-8" data-id="element-31">
        {/* Owner Breakdown */}
        <div data-id="element-32">
          <h4 className="text-sm font-semibold text-slate-700 mb-3" data-id="element-33">
            By Owner
          </h4>
          <HorizontalStackedBar segments={ownerSegments} total={totalAssets} baseCurrency={baseCurrency} data-id="element-34" />
        </div>

        <div className="border-t border-slate-100" data-id="element-35" />

        {/* Asset Class Breakdown (replaces simple Liquid vs Illiquid when RE exists) */}
        <div data-id="element-36">
          <h4 className="text-sm font-semibold text-slate-700 mb-3" data-id="element-37">
            {hasRealEstate ? 'By Asset Class' : 'Liquid vs Illiquid'}
          </h4>
          <HorizontalStackedBar segments={hasRealEstate ? assetClassSegments : typeSegments} total={totalAssets} baseCurrency={baseCurrency} data-id="element-38" />
        </div>

        {/* Real Estate Equity vs Debt — only when RE assets exist */}
        {hasRealEstate && reEquityDebtSegments.length > 0 && <>
            <div className="border-t border-slate-100" data-id="element-39" />
            <div data-id="element-40">
              <h4 className="text-sm font-semibold text-slate-700 mb-1" data-id="element-41">
                Real Estate: Equity vs Debt
              </h4>
              <p className="text-xs text-slate-400 mb-3" data-id="element-42">
                Of {formatCurrency(reGrossValue, baseCurrency)} gross property
                value
              </p>
              <HorizontalStackedBar segments={reEquityDebtSegments} total={reGrossValue} baseCurrency={baseCurrency} data-id="element-43" />
            </div>
          </>}

        <div className="border-t border-slate-100" data-id="element-44" />

        {/* Currency Breakdown */}
        <div data-id="element-45">
          <h4 className="text-sm font-semibold text-slate-700 mb-3" data-id="element-46">
            By Currency
          </h4>
          <HorizontalStackedBar segments={currencySegments} total={totalAssets} baseCurrency={baseCurrency} data-id="element-47" />
        </div>

        <div className="border-t border-slate-100" data-id="element-48" />

        {/* Country Breakdown */}
        <div data-id="element-49">
          <h4 className="text-sm font-semibold text-slate-700 mb-3" data-id="element-50">
            By Country
          </h4>
          <HorizontalStackedBar segments={countrySegments} total={totalAssets} baseCurrency={baseCurrency} data-id="element-51" />
        </div>
      </CardContent>
    </Card>;
}